import {useTranslation} from "react-i18next";
import * as React from "react";
import {useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridOptions} from "ag-grid-community";
import {formatDate} from "../../../utils/FormatUtils";
import ColouredChip from "../../../component/ColouredChip";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {
    COLOR_BILLABLE,
    COLOR_BILLED_PAID,
    COLOR_BILLED_UNPAID,
    COLOR_REJECTED,
    ID_BILLABLE,
    ID_BILLED_PAID,
    ID_BILLED_UNPAID,
    ID_REJECTED
} from "../constants/ETollConstants";
import {IconButton, Tooltip} from "@mui/material";
import Chip from "@mui/material/Chip";
import {InfoRounded} from "@mui/icons-material";
import ETollMerfDetails from "./ETollMerfDetails";


const ETollReportDetailTable = (props: any) => {
    const {t} = useTranslation('finance', {keyPrefix: 'etoll'});
    const gridRef = useRef<AgGridReact>(null);
    const [tollDetails, setTollDetails] = useState<any>();
    const [openTollDetails, setOpenTollDetails] = useState<any>();

    const columnDefs: ColDef[] = [
        {
            field: 'report.fileName',
            headerName: t('tables.charge.reportName'),
            headerTooltip: t('tables.charge.reportName'),
            suppressSizeToFit: true,
            minWidth: 250,
            unSortIcon: true,
            sortable: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'equipmentNumber',
            headerName: t('tables.charge.equipmentNumber'),
            headerTooltip: t('tables.charge.equipmentNumber'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'registration',
            headerName: t('tables.charge.plate'),
            headerTooltip: t('tables.charge.plate'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'entryDate',
            headerName: t('tables.charge.entryDate'),
            headerTooltip: t('tables.charge.entryDate'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'exitTollName',
            headerName: t('tables.charge.exitTollName'),
            headerTooltip: t('tables.charge.exitTollName'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'vatRate',
            headerName: t('tables.charge.vatRate'),
            headerTooltip: t('tables.charge.vatRate'),
            minWidth: 150,
            unSortIcon: true,
            cellRenderer: (params: any) =>  params.value ? params.value === 1 ? "23%" : params.value === 2 ? "6%" : "" : ""
        },
        {
            field: 'fareAmount',
            headerName: t('tables.charge.fareAmount'),
            headerTooltip: t('tables.charge.fareAmount'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'customerId',
            headerName: t('tables.charge.customerId'),
            headerTooltip: t('tables.charge.customerId'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'raCode',
            headerName: t('tables.charge.raCode'),
            headerTooltip: t('tables.charge.raCode'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'status', headerName: t('tables.charge.status'), headerTooltip: t('tables.charge.status'),
            minWidth: 250, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            tooltipField: 'data.pgwDescription',
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_REJECTED:
                        return {
                            component: () =>
                                <Tooltip title={t('tables.charge.tollErrorDetails')}>
                                    <>
                                        <Chip label={t(`statuses.REJECTED`)} color={COLOR_REJECTED}/>
                                        <span>
                                            <IconButton color="accent" onClick={() => {
                                                {
                                                    setTollDetails(params.data);
                                                    setOpenTollDetails(true);
                                                }
                                            }}>
                                                <InfoRounded/>
                                            </IconButton>
                                        </span>
                                    </>
                                </Tooltip>
                        };
                    case ID_BILLABLE:
                        return {
                            component: () => <ColouredChip name={ID_BILLABLE} color={COLOR_BILLABLE}/>
                        };
                    case ID_BILLED_PAID:
                        return {
                            component: () => <ColouredChip name={ID_BILLED_PAID} color={COLOR_BILLED_PAID}/>
                        }
                    case ID_BILLED_UNPAID:
                        return {
                            component: () =>
                                <Tooltip title={t('tables.charge.tollErrorDetails')}>
                                    <>
                                        <Chip label={t(`statuses.BILLED_UNPAID`)} color={COLOR_BILLED_UNPAID}/>
                                        <span>
                                            <IconButton color="accent" onClick={() => {
                                                {
                                                    setTollDetails(params.data);
                                                    setOpenTollDetails(true);
                                                }
                                            }}>
                                                <InfoRounded/>
                                            </IconButton>
                                        </span>
                                    </>
                                </Tooltip>
                        }
                    default:
                        break;
                }
            }
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {title: t('tables.charge.no-rows-overlay.title')}
    };


    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
        <>
            <AgGridCustomTable
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowData={props.data} rowBuffer={0}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.sizeColumnsToFit();
                    gridRef.current!.api.hideOverlay();
                }}
            />
            <ETollMerfDetails tollDetails={tollDetails} open={openTollDetails}
                              onClose={() => {
                                  setOpenTollDetails(false);
                              }}/>
        </>
    );

}

export default ETollReportDetailTable;